import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT, UserService } from '@cfp/core';
import { NotesList } from '@cfp/features-shared/notes';
import {
  ReportAppendicesPages,
  ReportEnergyLabelsPages,
  ReportInvestmentPages,
  ReportPage
} from '@cfp/features-shared/portfolio-buildings-roadmap-report';
import { ReportCoverPage, ReportFooter } from '@cfp/features-shared/reports/report';
import { EnergyLabelService } from '@cfp/shared/energy-label';
import { PortfolioLayoutPageBase } from '@cfp/shared/portfolio/layout';
import { PrintableContent } from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import {
  CustomerByPortfolioService,
  EnergyLabelTypeDTO,
  LabelGroupDTO,
  LloydsDecarbonisationPathwayDTO,
  LloydsPortfolioRoadmapReportService,
  LloydsRoadmapPortfolioDetailsDTO,
  OwnershipStatusDTO,
  PortfolioNotesService
} from 'csr-asset-app';
import { catchError, combineLatest, map, mergeMap, of } from 'rxjs';
import {
  LBG_BUILDING_PATHWAY_EMBEDDED_HEIGHT,
  LbgReportBuildingDecarbonisationPage
} from './components/lbg-report-building-decarbonisation-page';
import { mapLloydsRoadmapToPortfolioBuildingsRoadmap } from './map-lloyds-roadmap-to-portfolio-buildings-roadmap';
import { LbgReportBuildingsPages } from './pages/report-buildings-pages';
import { LbgReportDecarbonisationPathwayPages } from './pages/report-decarbonisation-pathway-pages';
import { LbgReportEnergyConsumptionPages } from './pages/report-energy-consumption-pages';

const TRANSLATION_KEY_PREFIX = 'lbgPortfolioRoadmapReport';

@Component({
  selector: 'cfp-lbg-portfolio-roadmap-report',
  templateUrl: 'lbg-portfolio-roadmap-report.html',
  styleUrls: ['lbg-portfolio-roadmap-report.scss'],
  imports: [
    PrintableContent,
    ReportPage,
    ReportCoverPage,
    ReportFooter,
    LbgReportBuildingsPages,
    LbgReportEnergyConsumptionPages,
    ReportEnergyLabelsPages,
    ReportInvestmentPages,
    LbgReportDecarbonisationPathwayPages,
    ReportAppendicesPages,
    LbgReportBuildingDecarbonisationPage,
    NotesList,
    TranslateModule,
    NgTemplateOutlet
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LbgPortfolioRoadmapReport extends PortfolioLayoutPageBase {
  protected readonly environment = inject(ENVIRONMENT);
  private readonly customerByPortfolioService = inject(CustomerByPortfolioService);
  private readonly userService = inject(UserService);
  private readonly lloydsPortfolioRoadmapReportService = inject(LloydsPortfolioRoadmapReportService);
  private readonly energyLabelService = inject(EnergyLabelService);
  private readonly portfolioNotesService = inject(PortfolioNotesService);

  protected readonly translationKeyPrefix = TRANSLATION_KEY_PREFIX;
  protected readonly buildingPathwayPageHeight = LBG_BUILDING_PATHWAY_EMBEDDED_HEIGHT;
  protected readonly coverLogo = this.environment.images.logoReport || this.environment.images.logoNavbar;
  protected readonly headerLogo = `${this.environment.customerAssetsDir}img/${this.coverLogo}`;
  protected readonly showExplanationPage = this.environment.components.portfolioReportShowExplanationPage === true;
  /** Set when portfolio map markers are idle (or map load failed) so PDF capture includes pins. */
  protected readonly mapReadyForPrint = signal(false);

  protected currentDate = new Date();

  protected readonly reportDataLoaded = computed(() => !!this.portfolioBuildingsRoadmap() && this.mapReadyForPrint());

  protected readonly customerName = toSignal(
    toObservable(this.portfolioId).pipe(
      mergeMap((portfolioId) => {
        if (!portfolioId) {
          return of('');
        }
        return this.customerByPortfolioService
          .v2CustomerByPortfolioIdGet(portfolioId)
          .pipe(map((customer) => customer.name));
      }),
      catchError(() => of(''))
    ),
    {
      initialValue: ''
    }
  );

  protected readonly hasPortfolioNotesLicense = toSignal(this.userService.hasLicense('portfolio_notes'));

  protected readonly lloydsRoadmap = toSignal<LloydsRoadmapPortfolioDetailsDTO | undefined>(
    toObservable(this.portfolioId).pipe(
      mergeMap((portfolioId) => {
        if (!portfolioId) {
          return of(undefined);
        }
        return this.lloydsPortfolioRoadmapReportService.v2LloydsPortfoliosIdRoadmapReportGet(portfolioId);
      }),
      catchError(() => of(undefined))
    )
  );

  protected readonly portfolioBuildingsRoadmap = computed(() => {
    const lloydsRoadmap = this.lloydsRoadmap();
    if (!lloydsRoadmap) {
      return undefined;
    }

    const portfolioBuildingsRoadmap = mapLloydsRoadmapToPortfolioBuildingsRoadmap(lloydsRoadmap);
    portfolioBuildingsRoadmap.buildings.sort(
      (a, b) =>
        (a.energyLabels
          ? this.energyLabelService.getSortIndex(LabelGroupDTO.EPC, a.energyLabels.currentEnergyLabel.label)
          : 1000) -
        (b.energyLabels
          ? this.energyLabelService.getSortIndex(LabelGroupDTO.EPC, b.energyLabels.currentEnergyLabel.label)
          : 1000)
    );
    return portfolioBuildingsRoadmap;
  });

  protected readonly residentialDecarbonisationPathway = computed(
    () => this.lloydsRoadmap()?.residentialDecarbonisationPathway
  );

  protected readonly nonResidentialDecarbonisationPathway = computed(
    () => this.lloydsRoadmap()?.nonResidentialDecarbonisationPathway
  );

  protected readonly buildingDecarbonisationPathways = computed(() => {
    const lloydsRoadmap = this.lloydsRoadmap();
    if (!lloydsRoadmap) {
      return undefined;
    }

    const pathwaysByBuildingId = new Map<number, LloydsDecarbonisationPathwayDTO>();
    lloydsRoadmap.buildings.forEach((building) => {
      if (building.decarbonisationPathway) {
        pathwaysByBuildingId.set(building.id, building.decarbonisationPathway);
      }
    });
    return pathwaysByBuildingId;
  });

  protected readonly portfolioNotes = toSignal(
    combineLatest([toObservable(this.portfolioId), toObservable(this.hasPortfolioNotesLicense)]).pipe(
      mergeMap(([portfolioId, hasPortfolioNotesLicense]) => {
        if (!portfolioId || !hasPortfolioNotesLicense) {
          return of(undefined);
        }
        return this.portfolioNotesService.v2PortfoliosPortfolioIdNotesGet(portfolioId);
      })
    )
  );

  protected readonly buildingsWithPlan = computed(() =>
    this.portfolioBuildingsRoadmap()?.buildings.filter((buildingData) => buildingData.roadmap != null)
  );

  protected readonly buildingsForAppendices = computed(() =>
    this.portfolioBuildingsRoadmap()?.buildings.filter((buildingData) => {
      if (buildingData.roadmap != null) {
        return true;
      }
      if (buildingData.building.ownershipStatus === OwnershipStatusDTO.PLANSTOSELLBEFORE2030) {
        return true;
      }
      if (
        buildingData.energyLabels?.currentEnergyLabel?.labelType === EnergyLabelTypeDTO.CALCULATEDCURRENT &&
        buildingData.energyLabels?.currentEnergyLabel?.label[0] === 'A'
      ) {
        return true;
      }
      return false;
    })
  );

  protected readonly years = computed(() =>
    this.portfolioBuildingsRoadmap()?.totalInvestmentPerYear.map(
      (totalInvestmentPerYear) => totalInvestmentPerYear.year
    )
  );
}
